import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowLeft, GoogleLogo, EnvelopeSimple, LockKey, DeviceMobile, User } from "@phosphor-icons/react";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { startMsg91Login } from "../lib/msg91";

const COUNTRY_CODES = [
  { code: "+91", name: "India" },
  { code: "+1", name: "USA" },
  { code: "+44", name: "UK" },
  { code: "+971", name: "UAE" },
  { code: "+65", name: "Singapore" },
  { code: "+61", name: "Australia" },
];

const composeMobile = (code, num) => code + (num || "").replace(/\D/g, "").replace(/^0+/, "");

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, login, register, verifyOtp, mobileSignup, mobileVerify, msg91Verify } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") === "organizer" ? "organizer" : "customer");
  const [regMethod, setRegMethod] = useState("email"); // email | mobile
  const [form, setForm] = useState({ email: "", password: "", otp: "", first_name: "", last_name: "", new_password: "", name: "", mobileNum: "", mobile: "", code: "+91" });
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.role === "organizer" ? "/organizer" : "/discover", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const routeAfterAuth = (u) => {
    if (u.role === "organizer") navigate("/organizer");
    else if (u.role === "admin") navigate("/admin");
    else if (!u.first_name) setMode("name");
    else navigate("/discover");
  };

  const doLogin = async () => {
    setLoading(true);
    try {
      const u = await login(form.email.trim(), form.password.trim());
      routeAfterAuth(u);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.reason === "unverified") {
        setMode("otp"); setCooldown(30);
        toast.info("Please verify your email. A code was sent.");
      } else toast.error(apiErr(e));
    } finally { setLoading(false); }
  };

  const doRegister = async () => {
    if (regMethod === "mobile") return doMobileSignup();
    setLoading(true);
    try {
      await register({ email: form.email, password: form.password, role });
      setMode("otp"); setCooldown(30);
      toast.success("OTP sent to your email.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doVerify = async () => {
    setLoading(true);
    try {
      const u = await verifyOtp(form.email, form.otp);
      toast.success("Email verified!");
      routeAfterAuth(u);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doResend = async () => {
    try {
      const purpose = mode === "reset" ? "password_reset" : "email_verify";
      const { data } = await api.post("/auth/resend-otp", { email: form.email, purpose });
      setCooldown(data.cooldown || 30);
      toast.success("New code sent.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doName = async () => {
    setLoading(true);
    try {
      await api.put("/account/profile", { first_name: form.first_name, last_name: form.last_name });
      navigate("/discover");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doForgot = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setMode("reset");
      toast.success("Reset code sent (if the account exists).");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doReset = async () => {
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email: form.email, otp: form.otp, new_password: form.new_password });
      toast.success("Password reset. Please log in.");
      setMode("login");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doMobileRequest = async () => {
    const mobile = composeMobile(form.code, form.mobileNum);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mobile/request", { mobile });
      setForm((f) => ({ ...f, mobile })); setMode("mobile-otp"); setCooldown(data.cooldown || 30);
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doMobileSignup = async () => {
    const mobile = composeMobile(form.code, form.mobileNum);
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    setLoading(true);
    try {
      const data = await mobileSignup(mobile, form.name.trim(), role);
      setForm((f) => ({ ...f, mobile })); setMode("mobile-otp"); setCooldown(30);
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doMobileVerify = async () => {
    setLoading(true);
    try { const u = await mobileVerify(form.mobile, form.otp); toast.success("Logged in!"); routeAfterAuth(u); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doMsg91 = async () => {
    setLoading(true);
    try {
      const accessToken = await startMsg91Login();
      const u = await msg91Verify(accessToken);
      toast.success("Logged in!");
      routeAfterAuth(u);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Direct Google OAuth 2.0 (Authorization Code flow). Redirect URI must match Google Console exactly.
    const redirectUri = window.location.origin + "/auth/google";
    // Role (for register) is carried in the OAuth state param and returned on the callback.
    const state = mode === "register" ? `role=${role}` : "";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      prompt: "select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const titles = {
    login: "Welcome back", register: "Create your account", otp: "Verify your email",
    name: "Tell us your name", forgot: "Reset your password", reset: "Set a new password",
    mobile: "Log in with mobile", "mobile-otp": "Enter the code",
  };

  const CountryCode = () => (
    <Select value={form.code} onValueChange={(v) => setForm((f) => ({ ...f, code: v }))}>
      <SelectTrigger data-testid="country-code" className="w-[110px] h-11 shrink-0"><SelectValue /></SelectTrigger>
      <SelectContent>
        {COUNTRY_CODES.map((c) => (
          <SelectItem key={c.code} value={c.code} data-testid={`cc-${c.code}`}>{c.name} {c.code}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const submitFn = { login: doLogin, register: doRegister, otp: doVerify, name: doName, forgot: doForgot, reset: doReset, mobile: doMobileRequest, "mobile-otp": doMobileVerify }[mode];
  const submitLabel = { login: "Log in", register: regMethod === "mobile" ? "Sign up with mobile" : "Create account", otp: "Verify", name: "Continue", forgot: "Send reset code", reset: "Reset password", mobile: "Send OTP", "mobile-otp": "Verify & log in" }[mode];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block bg-foreground grain overflow-hidden">
        <img src="https://images.unsplash.com/photo-1565035010268-a3816f98589a?w=1200&q=80&auto=format&fit=crop"
          alt="concert" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-[2] h-full flex flex-col justify-between p-12 text-white">
          <Logo dark />
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-semibold mb-4">
              <Sparkle weight="fill" size={14} className="text-primary" /> AI-first events platform
            </span>
            <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight">
              Book unforgettable<br />experiences in seconds.
            </h2>
            <p className="text-white/60 mt-3 max-w-sm">Discover with AI, pay securely, and carry your ticket in your pocket.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={16} /> Home</Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{titles[mode]}</h1>

          {mode === "register" && (
            <>
              <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-full bg-muted">
                {["customer", "organizer"].map((r) => (
                  <button key={r} onClick={() => setRole(r)} data-testid={`role-${r}`}
                    className={`h-9 rounded-full text-sm font-semibold capitalize transition-colors ${role === r ? "bg-foreground text-background" : "text-muted-foreground"}`}>
                    {r}
                  </button>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 p-1 rounded-full bg-muted">
                {[["email", "Email"], ["mobile", "Mobile"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRegMethod(v)} data-testid={`reg-method-${v}`}
                    className={`h-9 rounded-full text-sm font-semibold transition-colors ${regMethod === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mt-6 space-y-4">
            {mode === "register" && regMethod === "mobile" && (
              <div>
                <Label>Full name</Label>
                <div className="relative mt-1.5">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="signup-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="pl-10 h-11" />
                </div>
              </div>
            )}

            {(mode === "mobile" || (mode === "register" && regMethod === "mobile")) && (
              <div>
                <Label>Mobile number</Label>
                <div className="flex gap-2 mt-1.5">
                  <CountryCode />
                  <div className="relative flex-1">
                    <DeviceMobile size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="mobile-input" value={form.mobileNum} onChange={set("mobileNum")} placeholder="98xxxxxxxx" className="pl-10 h-11" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">We'll send a one-time code via SMS.</p>
              </div>
            )}

            {(["login", "forgot"].includes(mode) || (mode === "register" && regMethod === "email")) && (
              <div>
                <Label>Email</Label>
                <div className="relative mt-1.5">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="pl-10 h-11" />
                </div>
              </div>
            )}
            {(mode === "login" || (mode === "register" && regMethod === "email")) && (
              <div>
                <Label>Password</Label>
                <div className="relative mt-1.5">
                  <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="password-input" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" className="pl-10 h-11" />
                </div>
                {mode === "register" && <p className="text-xs text-muted-foreground mt-1">Min 8 chars with letters and numbers.</p>}
              </div>
            )}

            {["otp", "reset", "mobile-otp"].includes(mode) && (
              <div>
                <Label>Verification code</Label>
                <Input data-testid="otp-input" value={form.otp} onChange={set("otp")} placeholder="6-digit code" className="h-11 mt-1.5 font-mono tracking-widest text-lg" maxLength={6} />
                <button onClick={mode === "mobile-otp" ? doMobileRequest : doResend} disabled={cooldown > 0} className="text-xs text-primary mt-2 disabled:text-muted-foreground" data-testid="resend-otp">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            )}
            {mode === "reset" && (
              <div>
                <Label>New password</Label>
                <Input data-testid="new-password-input" type="password" value={form.new_password} onChange={set("new_password")} placeholder="••••••••" className="h-11 mt-1.5" />
              </div>
            )}
            {mode === "name" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name</Label><Input data-testid="first-name-input" value={form.first_name} onChange={set("first_name")} className="h-11 mt-1.5" /></div>
                <div><Label>Last name</Label><Input data-testid="last-name-input" value={form.last_name} onChange={set("last_name")} className="h-11 mt-1.5" /></div>
              </div>
            )}

            {mode === "login" && <button onClick={() => setMode("forgot")} className="text-sm text-primary" data-testid="forgot-link">Forgot password?</button>}

            <Button data-testid="auth-submit" onClick={submitFn} disabled={loading} className="w-full h-11 rounded-full">
              {loading ? "Please wait…" : submitLabel}
            </Button>

            {["login", "register"].includes(mode) && (
              <>
                <div className="flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px bg-border flex-1" /> OR <div className="h-px bg-border flex-1" /></div>
                <Button variant="outline" onClick={googleLogin} data-testid="google-btn" className="w-full h-11 rounded-full border-foreground/20">
                  <GoogleLogo weight="bold" size={18} className="mr-2" /> Continue with Google
                </Button>
                {mode === "login" && (
                  <Button variant="outline" onClick={doMsg91} disabled={loading} data-testid="msg91-mobile-otp-btn" className="w-full h-11 rounded-full border-foreground/20">
                    <DeviceMobile weight="bold" size={18} className="mr-2" /> Continue with Mobile OTP
                  </Button>
                )}
              </>
            )}
          </div>

          <div className="mt-6 text-sm text-muted-foreground text-center">
            {mode === "login" && <>New here? <button className="text-primary font-semibold" onClick={() => setMode("register")} data-testid="switch-register">Create an account</button></>}
            {mode === "register" && <>Already have an account? <button className="text-primary font-semibold" onClick={() => setMode("login")} data-testid="switch-login">Log in</button></>}
            {["forgot", "reset", "mobile", "mobile-otp"].includes(mode) && <button className="text-primary font-semibold" onClick={() => setMode("login")}>Back to login</button>}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
